/**
 * Engagements Page - 8-stage engagement pipeline kanban board
 * Stages: Research -> Strategy -> NDA -> Discovery -> Report -> Roadmap -> Proposal -> Contract
 */
import React, { useState, useEffect } from 'react';
import {
  Loader2,
  Plus,
  Search,
  Users,
  Lightbulb,
  Shield,
  FileText,
  Map,
  FileCheck,
  CheckCircle2,
  X,
  Mail,
  Phone,
  Globe,
  Clock,
  ArrowRight,
  ArrowLeft,
  Trash2,
  Upload,
  RefreshCw,
  Send,
  FileSignature,
  Mic,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Activity,
  Building2,
  Tag,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import dataService from '../services/data';
import {
  EngagementKanbanData,
  EngagementSummary,
  EngagementResponse,
  EngagementStage,
  EngagementCreate,
  EngagementStatsResponse,
  EngagementStageHistoryEntry,
  EngagementDocumentResponse
} from '../types';

const STAGES: EngagementStage[] = ['research', 'strategy', 'nda', 'discovery', 'report', 'roadmap', 'proposal', 'contract'];

const STAGE_CONFIG: Record<EngagementStage, {
  title: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
}> = {
  research: {
    title: 'Research',
    subtitle: 'AI Company Profiling',
    icon: Search,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    dotColor: 'bg-blue-500',
  },
  strategy: {
    title: 'Strategy',
    subtitle: 'Engagement Strategy',
    icon: Lightbulb,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    dotColor: 'bg-amber-500',
  },
  nda: {
    title: 'NDA',
    subtitle: 'Non-Disclosure Agreement',
    icon: Shield,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    dotColor: 'bg-indigo-500',
  },
  discovery: {
    title: 'Discovery',
    subtitle: 'Needs Discovery',
    icon: Users,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    dotColor: 'bg-purple-500',
  },
  report: {
    title: 'Report',
    subtitle: 'AI Analysis Report',
    icon: FileText,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    dotColor: 'bg-cyan-500',
  },
  roadmap: {
    title: 'Roadmap',
    subtitle: 'Technical Roadmap',
    icon: Map,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    dotColor: 'bg-teal-500',
  },
  proposal: {
    title: 'Proposal',
    subtitle: 'Executive Summary',
    icon: FileCheck,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    dotColor: 'bg-orange-500',
  },
  contract: {
    title: 'Contract',
    subtitle: 'Contract & Close',
    icon: CheckCircle2,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    dotColor: 'bg-green-500',
  },
};

const BUDGET_LABELS: Record<string, string> = {
  under_10k: '< $10K',
  '10k_50k': '$10K-$50K',
  '50k_100k': '$50K-$100K',
  over_100k: '$100K+',
};

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
};

type DetailTab = 'company' | 'stage_data' | 'ai_actions' | 'documents' | 'history';

const EngagementsPage: React.FC = () => {
  const [kanbanData, setKanbanData] = useState<EngagementKanbanData | null>(null);
  const [stats, setStats] = useState<EngagementStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState<EngagementResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('stage_data');
  const [documents, setDocuments] = useState<EngagementDocumentResponse[]>([]);
  const [history, setHistory] = useState<EngagementStageHistoryEntry[]>([]);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const [newEngagement, setNewEngagement] = useState<EngagementCreate>({
    company_name: '',
    company_website: '',
    company_industry: '',
    company_description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_title: '',
    priority: 'medium',
    tags: [],
    notes: '',
  });

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [kanbanResult, statsResult] = await Promise.all([
        dataService.fetchEngagementKanban(),
        dataService.fetchEngagementStats(),
      ]);

      if (kanbanResult.error) {
        toast.error(kanbanResult.error);
      } else {
        setKanbanData(kanbanResult.data || null);
      }

      if (statsResult.data) {
        setStats(statsResult.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateEngagement = async () => {
    if (!newEngagement.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setIsSubmitting(true);
    const result = await dataService.createEngagement(newEngagement);
    setIsSubmitting(false);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Engagement created — AI research started');
      setShowAddModal(false);
      setNewEngagement({
        company_name: '',
        company_website: '',
        company_industry: '',
        company_description: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        contact_title: '',
        priority: 'medium',
        tags: [],
        notes: '',
      });
      loadData();
    }
  };

  const handleViewEngagement = async (id: number) => {
    const result = await dataService.fetchEngagement(id);
    if (result.error) {
      toast.error(result.error);
    } else if (result.data) {
      setSelectedEngagement(result.data);
      setActiveTab('stage_data');
      setDocuments([]);
      setHistory([]);
      setShowDetailModal(true);
    }
  };

  const refreshSelectedEngagement = async () => {
    if (!selectedEngagement) return;
    const result = await dataService.fetchEngagement(selectedEngagement.id);
    if (result.data) {
      setSelectedEngagement(result.data);
    }
  };

  const handleTransitionStage = async (id: number, toStage: EngagementStage) => {
    const result = await dataService.transitionEngagementStage(id, { to_stage: toStage });
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(`Moved to ${STAGE_CONFIG[toStage].title}`);
      loadData();
      if (selectedEngagement && selectedEngagement.id === id && result.data) {
        setSelectedEngagement(result.data);
      }
    }
  };

  const handleDeleteEngagement = async (id: number) => {
    if (!confirm('Are you sure you want to archive this engagement?')) return;

    const result = await dataService.deleteEngagement(id);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Engagement archived');
      setShowDetailModal(false);
      setSelectedEngagement(null);
      loadData();
    }
  };

  // AI action handlers
  const handleAIAction = async (actionName: string, actionFn: () => Promise<any>) => {
    setIsActionLoading(actionName);
    try {
      const result = await actionFn();
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(`${actionName} completed`);
        await refreshSelectedEngagement();
      }
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleUploadAudio = async () => {
    if (!audioFile || !selectedEngagement) return;
    setIsActionLoading('Upload Audio');
    try {
      const result = await dataService.uploadEngagementAudio(selectedEngagement.id, audioFile);
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(`Audio transcribed: ${result.data?.transcript?.substring(0, 100)}...`);
        setAudioFile(null);
        await refreshSelectedEngagement();
        // Refresh documents if on that tab
        if (activeTab === 'documents') {
          const docsResult = await dataService.fetchEngagementDocuments(selectedEngagement.id);
          if (docsResult.data) setDocuments(docsResult.data);
        }
      }
    } finally {
      setIsActionLoading(null);
    }
  };

  const loadDocuments = async () => {
    if (!selectedEngagement) return;
    const result = await dataService.fetchEngagementDocuments(selectedEngagement.id);
    if (result.data) setDocuments(result.data);
  };

  const loadHistory = async () => {
    if (!selectedEngagement) return;
    const result = await dataService.fetchEngagementHistory(selectedEngagement.id);
    if (result.data) setHistory(result.data);
  };

  useEffect(() => {
    if (activeTab === 'documents' && selectedEngagement) loadDocuments();
    if (activeTab === 'history' && selectedEngagement) loadHistory();
  }, [activeTab, selectedEngagement?.id]);

  const addTag = () => {
    if (tagInput.trim() && !newEngagement.tags?.includes(tagInput.trim())) {
      setNewEngagement({
        ...newEngagement,
        tags: [...(newEngagement.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setNewEngagement({
      ...newEngagement,
      tags: newEngagement.tags?.filter(t => t !== tag) || [],
    });
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getNextStage = (currentStage: EngagementStage): EngagementStage | null => {
    const idx = STAGES.indexOf(currentStage);
    return idx < STAGES.length - 1 ? STAGES[idx + 1] : null;
  };

  const getPrevStage = (currentStage: EngagementStage): EngagementStage | null => {
    const idx = STAGES.indexOf(currentStage);
    return idx > 0 ? STAGES[idx - 1] : null;
  };

  const filteredKanbanData = kanbanData
    ? (Object.fromEntries(
        STAGES.map(stage => [
          stage,
          (kanbanData[stage] || []).filter(eng =>
            eng.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (eng.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase())
          ),
        ])
      ) as EngagementKanbanData)
    : null;

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Engagement Pipeline</h1>
            <p className="text-sm text-gray-600 mt-1">
              Track engagements through 8 stages from Research to Contract
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Engagement
          </button>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Engagements</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_engagements}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-green-500" />
                <p className="text-sm text-gray-500">Active</p>
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.active_engagements}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-blue-500" />
                <p className="text-sm text-gray-500">Contract Value</p>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {stats.total_contract_value ? formatCurrency(stats.total_contract_value) : '$0'}
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500 mb-2">Pipeline</p>
              <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
                {STAGES.map(stage => {
                  const count = stats.stage_counts[stage] || 0;
                  const total = stats.total_engagements || 1;
                  const pct = Math.max((count / total) * 100, count > 0 ? 4 : 0);
                  return (
                    <div
                      key={stage}
                      className={`${STAGE_CONFIG[stage].dotColor} transition-all`}
                      style={{ width: `${pct}%` }}
                      title={`${STAGE_CONFIG[stage].title}: ${count}`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search engagements..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Kanban Board - Horizontal Scroll */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading pipeline...</span>
          </div>
        ) : filteredKanbanData ? (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4" style={{ minWidth: `${STAGES.length * 256}px` }}>
              {STAGES.map(stage => {
                const config = STAGE_CONFIG[stage];
                const Icon = config.icon;
                const engagements = filteredKanbanData[stage] || [];

                return (
                  <div
                    key={stage}
                    className={`w-[240px] flex-shrink-0 rounded-xl border-2 ${config.borderColor} ${config.bgColor} overflow-hidden flex flex-col`}
                  >
                    {/* Column Header */}
                    <div className="p-3 border-b border-gray-200 bg-white">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold text-sm ${config.color}`}>{config.title}</h3>
                          <p className="text-xs text-gray-400 truncate">{config.subtitle}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
                          {engagements.length}
                        </span>
                      </div>
                    </div>

                    {/* Cards */}
                    <div className="p-2 space-y-2 min-h-[160px] max-h-[500px] overflow-y-auto flex-1">
                      {engagements.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <Icon className="w-6 h-6 mx-auto mb-1 opacity-50" />
                          <p className="text-xs">No engagements</p>
                        </div>
                      ) : (
                        engagements.map(eng => (
                          <div
                            key={eng.id}
                            onClick={() => handleViewEngagement(eng.id)}
                            className="bg-white rounded-lg border border-gray-200 p-2.5 cursor-pointer hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between gap-1">
                              <h4 className="font-medium text-sm text-gray-900 truncate flex-1">{eng.company_name}</h4>
                              {eng.priority && (
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${PRIORITY_COLORS[eng.priority] || 'bg-gray-100 text-gray-600'}`}>
                                  {eng.priority}
                                </span>
                              )}
                            </div>

                            {eng.contact_name && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate">{eng.contact_name}</p>
                            )}

                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {eng.budget_tier && (
                                <span className="text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">
                                  {BUDGET_LABELS[eng.budget_tier] || eng.budget_tier}
                                </span>
                              )}
                              {eng.contract_value != null && eng.contract_value > 0 && (
                                <span className="text-xs font-medium text-green-600">
                                  {formatCurrency(eng.contract_value)}
                                </span>
                              )}
                            </div>

                            {eng.tags && eng.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {eng.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                    {tag}
                                  </span>
                                ))}
                                {eng.tags.length > 2 && (
                                  <span className="px-1 text-gray-400 text-xs">+{eng.tags.length - 2}</span>
                                )}
                              </div>
                            )}

                            {eng.updated_at && (
                              <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400">
                                <Clock className="w-3 h-3" />
                                {new Date(eng.updated_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Failed to load pipeline data
          </div>
        )}

        {/* ============== Create Engagement Modal ============== */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">New Engagement</h2>
                  <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">AI research will run automatically after creation</p>
              </div>

              <div className="p-6 space-y-4">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={newEngagement.company_name}
                    onChange={e => setNewEngagement({ ...newEngagement, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Acme Corp"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      value={newEngagement.company_website || ''}
                      onChange={e => setNewEngagement({ ...newEngagement, company_website: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                    <input
                      type="text"
                      value={newEngagement.company_industry || ''}
                      onChange={e => setNewEngagement({ ...newEngagement, company_industry: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., SaaS, FinTech"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Description</label>
                  <textarea
                    value={newEngagement.company_description || ''}
                    onChange={e => setNewEngagement({ ...newEngagement, company_description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Brief description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={newEngagement.contact_name || ''}
                      onChange={e => setNewEngagement({ ...newEngagement, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Title</label>
                    <input
                      type="text"
                      value={newEngagement.contact_title || ''}
                      onChange={e => setNewEngagement({ ...newEngagement, contact_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="CEO, CTO..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                    <input
                      type="email"
                      value={newEngagement.contact_email || ''}
                      onChange={e => setNewEngagement({ ...newEngagement, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                    <input
                      type="tel"
                      value={newEngagement.contact_phone || ''}
                      onChange={e => setNewEngagement({ ...newEngagement, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 (555) ..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newEngagement.priority || 'medium'}
                    onChange={e => setNewEngagement({ ...newEngagement, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add tag..."
                    />
                    <button onClick={addTag} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {newEngagement.tags && newEngagement.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newEngagement.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded">
                          {tag}
                          <button onClick={() => removeTag(tag)} className="hover:text-blue-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newEngagement.notes || ''}
                    onChange={e => setNewEngagement({ ...newEngagement, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Initial notes..."
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
                <button
                  onClick={handleCreateEngagement}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Engagement
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============== Engagement Detail Modal ============== */}
        {showDetailModal && selectedEngagement && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className={`p-6 border-b ${STAGE_CONFIG[selectedEngagement.current_stage].bgColor} ${STAGE_CONFIG[selectedEngagement.current_stage].borderColor}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${STAGE_CONFIG[selectedEngagement.current_stage].bgColor} ${STAGE_CONFIG[selectedEngagement.current_stage].color}`}>
                        {STAGE_CONFIG[selectedEngagement.current_stage].title}
                      </span>
                      {selectedEngagement.priority && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${PRIORITY_COLORS[selectedEngagement.priority] || 'bg-gray-100 text-gray-600'}`}>
                          {selectedEngagement.priority} priority
                        </span>
                      )}
                      {selectedEngagement.budget_tier && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {BUDGET_LABELS[selectedEngagement.budget_tier] || selectedEngagement.budget_tier}
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-semibold mt-2">{selectedEngagement.company_name}</h2>
                    {selectedEngagement.contact_name && (
                      <p className="text-gray-600 text-sm">{selectedEngagement.contact_name}{selectedEngagement.contact_title ? ` — ${selectedEngagement.contact_title}` : ''}</p>
                    )}
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Stage Progress Bar */}
                <div className="mt-4 flex items-center gap-1">
                  {STAGES.map((stage, idx) => {
                    const currentIdx = STAGES.indexOf(selectedEngagement.current_stage);
                    const isPast = idx < currentIdx;
                    const isCurrent = idx === currentIdx;
                    const config = STAGE_CONFIG[stage];

                    return (
                      <React.Fragment key={stage}>
                        <div className="flex flex-col items-center" title={config.title}>
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                              isCurrent
                                ? `${config.dotColor} text-white border-transparent`
                                : isPast
                                ? 'bg-green-500 text-white border-transparent'
                                : 'bg-white text-gray-400 border-gray-300'
                            }`}
                          >
                            {isPast ? '\u2713' : idx + 1}
                          </div>
                          <span className={`text-xs mt-0.5 ${isCurrent ? config.color + ' font-medium' : 'text-gray-400'}`}>
                            {config.title}
                          </span>
                        </div>
                        {idx < STAGES.length - 1 && (
                          <div className={`flex-1 h-0.5 ${isPast ? 'bg-green-400' : 'bg-gray-200'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px overflow-x-auto">
                  {([
                    { id: 'company' as DetailTab, label: 'Company Info' },
                    { id: 'stage_data' as DetailTab, label: 'Stage Data' },
                    { id: 'ai_actions' as DetailTab, label: 'AI Actions' },
                    { id: 'documents' as DetailTab, label: 'Documents' },
                    { id: 'history' as DetailTab, label: 'History' },
                  ]).map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6 space-y-6">

                {/* Company Info Tab */}
                {activeTab === 'company' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedEngagement.contact_email && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${selectedEngagement.contact_email}`} className="hover:text-blue-600">
                            {selectedEngagement.contact_email}
                          </a>
                        </div>
                      )}
                      {selectedEngagement.contact_phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          {selectedEngagement.contact_phone}
                        </div>
                      )}
                      {selectedEngagement.company_website && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Globe className="w-4 h-4" />
                          <a href={selectedEngagement.company_website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                            {selectedEngagement.company_website}
                          </a>
                        </div>
                      )}
                      {selectedEngagement.company_industry && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Building2 className="w-4 h-4" />
                          {selectedEngagement.company_industry}
                        </div>
                      )}
                    </div>

                    {selectedEngagement.company_description && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-1">Description</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedEngagement.company_description}</p>
                      </div>
                    )}

                    {selectedEngagement.tags && selectedEngagement.tags.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedEngagement.tags.map(tag => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedEngagement.notes && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-1">Notes</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedEngagement.notes}</p>
                      </div>
                    )}
                  </>
                )}

                {/* Stage Data Tab */}
                {activeTab === 'stage_data' && (
                  <div className="space-y-4">
                    {/* Research: AI Company Profile */}
                    {selectedEngagement.ai_company_profile && (
                      <CollapsibleSection
                        title="AI Company Profile"
                        icon={<Search className="w-4 h-4 text-blue-600" />}
                        defaultOpen={true}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="ai_profile"
                      >
                        <div className="space-y-3 text-sm text-gray-600">
                          {Object.entries(selectedEngagement.ai_company_profile).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium text-gray-700 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                              {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Research: AI Strategy Suggestions */}
                    {selectedEngagement.ai_strategy_suggestions && selectedEngagement.ai_strategy_suggestions.length > 0 && (
                      <CollapsibleSection
                        title="AI Strategy Suggestions"
                        icon={<Lightbulb className="w-4 h-4 text-amber-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="ai_strategy"
                      >
                        <div className="space-y-3">
                          {selectedEngagement.ai_strategy_suggestions.map((suggestion, idx) => (
                            <div key={idx} className="bg-amber-50 rounded-lg p-3 text-sm">
                              <p className="font-medium text-amber-800">{suggestion.approach || `Strategy ${idx + 1}`}</p>
                              {suggestion.rationale && <p className="text-amber-700 mt-1">{suggestion.rationale}</p>}
                              {suggestion.risk_level && (
                                <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                  Risk: {suggestion.risk_level}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Strategy: Aaron's Strategy */}
                    {selectedEngagement.aaron_strategy && (
                      <CollapsibleSection
                        title="Engagement Strategy"
                        icon={<Lightbulb className="w-4 h-4 text-amber-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="aaron_strategy"
                      >
                        <div className="space-y-2 text-sm text-gray-600">
                          {Object.entries(selectedEngagement.aaron_strategy).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium text-gray-700 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                              {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* NDA Status */}
                    {selectedEngagement.nda_status && (
                      <CollapsibleSection
                        title="NDA Status"
                        icon={<Shield className="w-4 h-4 text-indigo-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="nda_status"
                      >
                        <div className="text-sm space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              selectedEngagement.nda_status === 'signed' ? 'bg-green-100 text-green-700' :
                              selectedEngagement.nda_status === 'sent' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {selectedEngagement.nda_status}
                            </span>
                          </div>
                          {selectedEngagement.nda_signer_name && <p className="text-gray-600">Signer: {selectedEngagement.nda_signer_name}</p>}
                          {selectedEngagement.nda_generated_at && <p className="text-gray-500">Generated: {new Date(selectedEngagement.nda_generated_at).toLocaleString()}</p>}
                          {selectedEngagement.nda_signed_at && <p className="text-green-600">Signed: {new Date(selectedEngagement.nda_signed_at).toLocaleString()}</p>}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Discovery */}
                    {(selectedEngagement.pain_point || selectedEngagement.discovery_notes) && (
                      <CollapsibleSection
                        title="Discovery Findings"
                        icon={<Users className="w-4 h-4 text-purple-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="discovery"
                      >
                        <div className="text-sm space-y-2">
                          {selectedEngagement.pain_point && (
                            <div><span className="font-medium text-gray-700">Pain Point:</span> {selectedEngagement.pain_point}</div>
                          )}
                          {selectedEngagement.budget_tier && (
                            <div><span className="font-medium text-gray-700">Budget:</span> {BUDGET_LABELS[selectedEngagement.budget_tier]}</div>
                          )}
                          {selectedEngagement.timeline && (
                            <div><span className="font-medium text-gray-700">Timeline:</span> {selectedEngagement.timeline}</div>
                          )}
                          {selectedEngagement.discovery_notes && (
                            <div><span className="font-medium text-gray-700">Notes:</span> {selectedEngagement.discovery_notes}</div>
                          )}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Report */}
                    {selectedEngagement.ai_report && (
                      <CollapsibleSection
                        title="AI Analysis Report"
                        icon={<FileText className="w-4 h-4 text-cyan-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="report"
                      >
                        <div className="space-y-2 text-sm text-gray-600">
                          {Object.entries(selectedEngagement.ai_report).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium text-gray-700 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                              {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                            </div>
                          ))}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Roadmap */}
                    {selectedEngagement.technical_roadmap && (
                      <CollapsibleSection
                        title="Technical Roadmap"
                        icon={<Map className="w-4 h-4 text-teal-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="roadmap"
                      >
                        <div className="space-y-2 text-sm text-gray-600">
                          {selectedEngagement.technical_roadmap.phases ? (
                            selectedEngagement.technical_roadmap.phases.map((phase: any, idx: number) => (
                              <div key={idx} className="bg-teal-50 rounded-lg p-3">
                                <p className="font-medium text-teal-800">{phase.name || `Phase ${idx + 1}`}</p>
                                {phase.description && <p className="text-teal-700 mt-1">{phase.description}</p>}
                                {phase.duration && <p className="text-xs text-teal-600 mt-1">Duration: {phase.duration}</p>}
                              </div>
                            ))
                          ) : (
                            Object.entries(selectedEngagement.technical_roadmap).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium text-gray-700 capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                                {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                              </div>
                            ))
                          )}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Proposal */}
                    {selectedEngagement.ai_executive_summary && (
                      <CollapsibleSection
                        title="Executive Summary / Proposal"
                        icon={<FileCheck className="w-4 h-4 text-orange-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="proposal"
                      >
                        <div className="text-sm text-gray-600 whitespace-pre-wrap">
                          {selectedEngagement.ai_executive_summary}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Contract */}
                    {selectedEngagement.contract_status && (
                      <CollapsibleSection
                        title="Contract"
                        icon={<CheckCircle2 className="w-4 h-4 text-green-600" />}
                        expanded={expandedSections}
                        toggle={toggleSection}
                        sectionKey="contract"
                      >
                        <div className="text-sm space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              selectedEngagement.contract_status === 'signed' ? 'bg-green-100 text-green-700' :
                              selectedEngagement.contract_status === 'sent' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {selectedEngagement.contract_status}
                            </span>
                            {selectedEngagement.contract_value != null && (
                              <span className="font-medium text-green-700">{formatCurrency(selectedEngagement.contract_value)}</span>
                            )}
                          </div>
                          {selectedEngagement.contract_signer_name && <p className="text-gray-600">Signer: {selectedEngagement.contract_signer_name}</p>}
                          {selectedEngagement.contract_signed_at && <p className="text-green-600">Signed: {new Date(selectedEngagement.contract_signed_at).toLocaleString()}</p>}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Empty state */}
                    {!selectedEngagement.ai_company_profile &&
                     !selectedEngagement.ai_strategy_suggestions?.length &&
                     !selectedEngagement.aaron_strategy &&
                     !selectedEngagement.nda_status &&
                     !selectedEngagement.pain_point &&
                     !selectedEngagement.ai_report &&
                     !selectedEngagement.technical_roadmap &&
                     !selectedEngagement.ai_executive_summary &&
                     !selectedEngagement.contract_status && (
                      <div className="text-center py-8 text-gray-400">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                        <p>No stage data yet. Use AI Actions to generate data.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Actions Tab */}
                {activeTab === 'ai_actions' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <AIActionButton
                        label="Re-run Research"
                        description="Re-generate AI company profile and strategy suggestions"
                        icon={<RefreshCw className="w-4 h-4" />}
                        loading={isActionLoading === 'Re-run Research'}
                        onClick={() => handleAIAction('Re-run Research', () =>
                          dataService.rerunEngagementResearch(selectedEngagement.id)
                        )}
                      />
                      <AIActionButton
                        label="Generate NDA"
                        description="Create a non-disclosure agreement"
                        icon={<Shield className="w-4 h-4" />}
                        loading={isActionLoading === 'Generate NDA'}
                        onClick={() => handleAIAction('Generate NDA', () =>
                          dataService.generateEngagementNDA(selectedEngagement.id)
                        )}
                      />
                      <AIActionButton
                        label="Generate Report"
                        description="Create AI analysis report"
                        icon={<FileText className="w-4 h-4" />}
                        loading={isActionLoading === 'Generate Report'}
                        onClick={() => handleAIAction('Generate Report', () =>
                          dataService.generateEngagementReport(selectedEngagement.id)
                        )}
                      />
                      <AIActionButton
                        label="Send Report"
                        description="Email the report to stakeholders"
                        icon={<Send className="w-4 h-4" />}
                        loading={isActionLoading === 'Send Report'}
                        onClick={() => handleAIAction('Send Report', () =>
                          dataService.sendEngagementReport(selectedEngagement.id)
                        )}
                      />
                      <AIActionButton
                        label="Generate Summary"
                        description="Create executive summary / proposal"
                        icon={<FileCheck className="w-4 h-4" />}
                        loading={isActionLoading === 'Generate Summary'}
                        onClick={() => handleAIAction('Generate Summary', () =>
                          dataService.generateEngagementSummary(selectedEngagement.id)
                        )}
                      />
                      <AIActionButton
                        label="Generate Contract"
                        description="Draft a contract document"
                        icon={<FileSignature className="w-4 h-4" />}
                        loading={isActionLoading === 'Generate Contract'}
                        onClick={() => handleAIAction('Generate Contract', () =>
                          dataService.generateEngagementContract(selectedEngagement.id)
                        )}
                      />
                    </div>

                    {/* Audio Upload */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Mic className="w-4 h-4 text-gray-600" />
                        <h4 className="font-medium text-gray-700 text-sm">Upload Audio for Transcription</h4>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">Upload a call recording (.mp3, .wav, .m4a, .webm, .flac, .ogg) for Whisper transcription and AI insights.</p>
                      <div className="flex items-center gap-3">
                        <label className="flex-1">
                          <input
                            type="file"
                            accept=".mp3,.wav,.m4a,.webm,.mp4,.flac,.ogg"
                            onChange={e => setAudioFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                        </label>
                        <button
                          onClick={handleUploadAudio}
                          disabled={!audioFile || isActionLoading === 'Upload Audio'}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm inline-flex items-center gap-2"
                        >
                          {isActionLoading === 'Upload Audio' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          Upload
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                  <div>
                    {documents.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <FileText className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">No documents yet</p>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-700">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {documents.map(doc => (
                              <tr key={doc.id}>
                                <td className="px-4 py-2 text-gray-900">{doc.file_name}</td>
                                <td className="px-4 py-2">
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded capitalize">
                                    {doc.document_type}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  {doc.status && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      doc.status === 'signed' ? 'bg-green-100 text-green-700' :
                                      doc.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {doc.status}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-gray-500">{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                  <div>
                    {history.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Clock className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">No stage transitions yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {history.map(entry => (
                          <div key={entry.id} className="flex items-start gap-3 border-l-2 border-gray-200 pl-4 py-2">
                            <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 -ml-[1.4rem] ${
                              entry.to_stage ? STAGE_CONFIG[entry.to_stage]?.dotColor || 'bg-gray-400' : 'bg-gray-400'
                            }`} />
                            <div>
                              <p className="text-sm text-gray-900">
                                {entry.from_stage ? (
                                  <>
                                    <span className="font-medium">{STAGE_CONFIG[entry.from_stage]?.title || entry.from_stage}</span>
                                    {' \u2192 '}
                                    <span className="font-medium">{STAGE_CONFIG[entry.to_stage]?.title || entry.to_stage}</span>
                                  </>
                                ) : (
                                  <>Created in <span className="font-medium">{STAGE_CONFIG[entry.to_stage]?.title || entry.to_stage}</span></>
                                )}
                              </p>
                              {entry.notes && <p className="text-xs text-gray-500 mt-0.5">{entry.notes}</p>}
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(entry.transitioned_at).toLocaleString()}
                                {entry.transitioned_by && ` by ${entry.transitioned_by}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer with stage transition buttons */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                <div>
                  {getPrevStage(selectedEngagement.current_stage) && (
                    <button
                      onClick={() => handleTransitionStage(selectedEngagement.id, getPrevStage(selectedEngagement.current_stage)!)}
                      className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm inline-flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to {STAGE_CONFIG[getPrevStage(selectedEngagement.current_stage)!].title}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDeleteEngagement(selectedEngagement.id)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                    title="Archive engagement"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {getNextStage(selectedEngagement.current_stage) && (
                    <button
                      onClick={() => handleTransitionStage(selectedEngagement.id, getNextStage(selectedEngagement.current_stage)!)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm inline-flex items-center gap-2"
                    >
                      Advance to {STAGE_CONFIG[getNextStage(selectedEngagement.current_stage)!].title}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ============== Helper Components ==============

const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  expanded: Record<string, boolean>;
  toggle: (key: string) => void;
  sectionKey: string;
}> = ({ title, icon, children, defaultOpen = false, expanded, toggle, sectionKey }) => {
  const isOpen = expanded[sectionKey] ?? defaultOpen;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => toggle(sectionKey)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-sm text-gray-700">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {isOpen && <div className="px-4 py-3">{children}</div>}
    </div>
  );
};

const AIActionButton: React.FC<{
  label: string;
  description: string;
  icon: React.ReactNode;
  loading: boolean;
  onClick: () => void;
}> = ({ label, description, icon, loading, onClick }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left transition-colors"
  >
    <div className="p-2 bg-blue-50 rounded-lg text-blue-600 flex-shrink-0">
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
    </div>
    <div>
      <p className="font-medium text-sm text-gray-900">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
    </div>
  </button>
);

export default EngagementsPage;
